import { useState } from 'react';
import { Play, Trash2 } from 'lucide-react';
import { useParams } from 'react-router';

import { useApplyPattern } from '@/api/pattern/use-apply-pattern.ts';
import { useDeletePattern } from '@/api/pattern/use-delete-pattern.ts';
import { useSuggestParties } from '@/api/pattern/use-suggest-parties.ts';
import { useUpdatePattern } from '@/api/pattern/use-update-pattern.ts';
import type { CategoryDto, PatternDto } from '@/api/types';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import { formatPrice, parsePrice } from '@/helpers/formatters.ts';

type PatternRowProps = {
  pattern: PatternDto;
  categories?: CategoryDto[];
};

const PatternRow = ({ pattern, categories }: PatternRowProps) => {
  const { projectId } = useParams();

  const [party, setParty] = useState(pattern.party);
  const [amount, setAmount] = useState(pattern.amount != null ? String(pattern.amount / 100) : '');
  const [description, setDescription] = useState(pattern.description || '');
  const [runResult, setRunResult] = useState<string | null>(null);

  const { data: partySuggestions } = useSuggestParties(projectId, party);
  const { mutate: updatePattern, isPending: updatePending } = useUpdatePattern(projectId!);
  const { mutate: deletePattern, isPending: deletePending } = useDeletePattern(projectId!);
  const { mutateAsync: applyPattern, isPending: applyPending } = useApplyPattern(projectId!);

  const isDirty =
    party !== pattern.party ||
    amount !== (pattern.amount != null ? String(pattern.amount / 100) : '') ||
    description !== (pattern.description || '');

  const handleSave = () => {
    updatePattern({
      uuid: pattern.uuid,
      data: {
        party,
        amount: amount ? parsePrice(Number(amount)) : undefined,
        description: description || undefined,
      },
    });
  };

  const handleCategoryChange = (categoryId: string) => {
    if (categoryId) {
      updatePattern({ uuid: pattern.uuid, data: { categoryId } });
    }
  };

  const handleDelete = () => {
    if (confirm('Delete this pattern?')) {
      deletePattern(pattern.uuid);
    }
  };

  const handleApply = () => {
    applyPattern(pattern.uuid)
      .then(({ updated }) => setRunResult(`Updated ${updated} transfer(s)`))
      .catch(() => undefined);
  };

  return (
    <div className="flex flex-col gap-2 border rounded-lg p-2 text-xs">
      <div className="flex items-center gap-2">
        <Input
          className="flex-1"
          list={`party-suggestions-${pattern.uuid}`}
          value={party}
          onChange={(event) => setParty(event.target.value)}
        />
        <datalist id={`party-suggestions-${pattern.uuid}`}>
          {partySuggestions?.map((suggestion) => (
            <option key={suggestion} value={suggestion} />
          ))}
        </datalist>
        <Input
          className="w-24"
          type="number"
          step="0.01"
          placeholder="Any amount"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
        />
        <select
          className="h-7 rounded-md border border-input bg-input/20 dark:bg-input/30 px-2 text-xs"
          value={pattern.category.uuid}
          onChange={(event) => handleCategoryChange(event.target.value)}
        >
          {categories?.map((category) => (
            <option key={category.uuid} value={category.uuid}>
              {category.name}
            </option>
          ))}
        </select>
        <Button variant="outline" size="icon" disabled={deletePending} onClick={handleDelete}>
          <Trash2 className="size-3.5" />
        </Button>
      </div>
      <div className="flex items-center gap-2">
        <Input
          className="flex-1"
          placeholder="Description (comma separated items)"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
        {isDirty && (
          <Button size="sm" disabled={updatePending} onClick={handleSave}>
            Save
          </Button>
        )}
        <Button variant="outline" size="sm" disabled={applyPending} onClick={handleApply}>
          <Play className="size-3.5" />
          Run
        </Button>
        {runResult && <span className="text-muted-foreground">{runResult}</span>}
      </div>
      {pattern.amount == null ? (
        <p className="text-muted-foreground">Matches any amount for "{pattern.party}"</p>
      ) : (
        <p className="text-muted-foreground">
          Matches {formatPrice(pattern.amount)} exactly for "{pattern.party}"
        </p>
      )}
    </div>
  );
};

export default PatternRow;
